import type { SRT_RowSelectionState } from '@/shared/components/master-table'
import type {
	OptimizationGroupModel,
	PricingCampaignCopySettingsResponseModel,
	PricingCampaignModel,
	PricingCampaignSettingsModel,
} from '@/shared/generated'

import React, { useCallback, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { filter, isString, keyBy, map, pick, prop } from 'lodash/fp'
import {
	AlertTriangleIcon,
	ArrowLeftIcon,
	CheckCircle2Icon,
	ChevronLeftIcon,
	ChevronRightIcon,
	InfoIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import z from 'zod'

import { SelectPricingCampaigns } from '@/features/select-pricing-campaigns'

import {
	useOptimizationGroupsQuery,
	useWithAffectedOGsHandler,
} from '@/entities/optimization-group'
import {
	PricingCampaignManagementResults,
	useCopyPricingCampaignSettingsMutation,
	usePricingCampaignQuery,
	usePricingCampaignsQuery,
} from '@/entities/pricing-campaign'

import { ErrorBoundaryWithUser } from '@/shared/components/ErrorBoundary'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Button } from '@/shared/components/ui/button'
import {
	Empty,
	EmptyContent,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from '@/shared/components/ui/empty'
import {
	Modal,
	ModalBody,
	ModalClose,
	ModalContent,
	ModalDescription,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import { Spinner } from '@/shared/components/ui/spinner'

import { getKeyTitle } from '../lib/helpers'
import SelectSettingsView from './SelectSettingsView'

const formDataSchema = z.object({
	settings: z.array(z.string()).min(1, 'Please select at least one setting'),
	campaigns: z
		.array(z.string())
		.min(1, 'Please select at least one pricing campaign'),
})
type FormDataType = z.infer<typeof formDataSchema>

type ContextValue = {
	pricingCampaignId: string
	step: 'settings' | 'campaigns' | 'confirmation' | 'processing'
	setStep: (
		step: 'settings' | 'campaigns' | 'confirmation' | 'processing'
	) => void
	draft: FormDataType
	setDraft: (data: FormDataType) => void
	submit: () => void
	copySettingsMutation: ReturnType<
		typeof useCopyPricingCampaignSettingsMutation
	>
}

type CopyPricingCampaignSettingsModalProps = {
	isOpen: boolean
	close(): void
	selected: string
	resetSelectedItems?(): void
	initialStep?: 'settings' | 'campaigns'
	selectedSetting?: string[]
}
const getPartialSettings = (
	pc: PricingCampaignModel | undefined,
	settingsKeys: string[]
) => {
	const keys = [...settingsKeys]

	return pick(keys, pc)?.settings || {}
}
export default function CopyPricingCampaignSettingsModal({
	isOpen,
	close,
	selected: selectedCampaign,
	resetSelectedItems,
	initialStep = 'settings',
	selectedSetting = [],
}: CopyPricingCampaignSettingsModalProps) {
	/**
	 * Common hooks
	 */
	const queryClient = useQueryClient()

	const [draft, setDraft] = useState<FormDataType>({
		settings: selectedSetting,
		campaigns: [],
	})
	const [step, setStep] = useState<
		'settings' | 'campaigns' | 'confirmation' | 'processing'
	>('settings')

	const pricingCampaignQuery = usePricingCampaignQuery(selectedCampaign)
	const baseCopySettingsMutation = useCopyPricingCampaignSettingsMutation({
		mutationKey: ['copyPricingCampaignSettings'],
		onSuccess() {
			resetSelectedItems && resetSelectedItems()
			queryClient.invalidateQueries({
				queryKey: ['pricing-campaigns'],
			})
			queryClient.invalidateQueries({
				queryKey: ['pricing-campaigns-overview'],
			})
			queryClient.invalidateQueries({
				queryKey: ['pricings-settings'],
			})
			queryClient.invalidateQueries({
				queryKey: ['products-counts-by-pc'],
			})
		},
	})

	/**
	 * Wrap with 424 handler to automatically handle affected optimization groups.
	 * This replaces both the manual pre-check and the old 424 error handling.
	 */
	const copySettingsMutation = useWithAffectedOGsHandler(
		baseCopySettingsMutation
	)
	/**
	 * The 424 handler automatically intercepts and handles affected optimization groups.
	 * No manual pre-check needed - backend is source of truth.
	 */
	const onApplyFormData = useCallback(() => {
		setStep('processing')
		copySettingsMutation.mutate({
			dst_pricing_campaign_ids: draft.campaigns,
			pricing_campaign_settings: getPartialSettings(
				pricingCampaignQuery.data,
				draft.settings
			) as PricingCampaignSettingsModel,
		})
	}, [copySettingsMutation, draft, pricingCampaignQuery.data])
	const context = useMemo(
		() => ({
			pricingCampaignId: selectedCampaign,
			step,
			setStep,
			draft,
			setDraft,
			submit: onApplyFormData,
			copySettingsMutation,
		}),
		[step, draft, selectedCampaign, copySettingsMutation, onApplyFormData]
	)

	return (
		<Modal
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) {
					close()
				}
			}}
		>
			<ModalContent className='max-w-5xl'>
				<ErrorBoundaryWithUser showDialog>
					{step === 'settings' && (
						<CopyPricingCampaignSettingsSettingsForm context={context} />
					)}
					{step === 'campaigns' && (
						<CopyPricingCampaignSettingsCampaignsForm context={context} />
					)}
					{step === 'confirmation' && (
						<CopyPricingCampaignSettingsConfirmation context={context} />
					)}
					{step === 'processing' && (
						<CopyPricingCampaignSettingsProcessing
							context={context}
							mutation={copySettingsMutation}
						/>
					)}
				</ErrorBoundaryWithUser>
			</ModalContent>
		</Modal>
	)
}

function CopyPricingCampaignSettingsSettingsForm({
	context,
}: {
	context: ContextValue
}) {
	const { t } = useTranslation()

	return (
		<>
			<ModalHeader>
				<ModalTitle>{t('pc.modal.copy_settings_1.title')}</ModalTitle>
				<ModalDescription>
					{t('pc.modal.copy_settings_1.subtitle')}
				</ModalDescription>
			</ModalHeader>
			<ModalBody className='space-y-3'>
				<SelectSettingsView
					pricingCmapaignId={context.pricingCampaignId}
					selected={context.draft.settings}
					setSelected={(settings) =>
						context.setDraft({ ...context.draft, settings })
					}
				/>
			</ModalBody>
			<ModalFooter className='justify-end space-x-2'>
				<ModalClose asChild>
					<Button className='mr-auto'>{t('general_cancel')}</Button>
				</ModalClose>
				<Button
					variant='primary'
					disabled={context.draft.settings.length === 0}
					onClick={() => context.setStep('campaigns')}
				>
					{t('general_next')}
					<ChevronRightIcon />
				</Button>
			</ModalFooter>
		</>
	)
}
function CopyPricingCampaignSettingsCampaignsForm({
	context,
}: {
	context: ContextValue
}) {
	const { t } = useTranslation()

	const [rowSelection, setRowSelection] = React.useState<SRT_RowSelectionState>(
		context.draft.campaigns?.reduce((acc, campaignId) => {
			acc[campaignId] = true

			return acc
		}, {} as SRT_RowSelectionState) || {}
	)

	return (
		<>
			<ModalHeader>
				<ModalTitle>{t('pc.modal.copy_settings_3.title')}</ModalTitle>
				<ModalDescription>
					{t('pc.modal.copy_settings_3.subtitle')}
				</ModalDescription>
			</ModalHeader>
			<ModalBody className='space-y-3'>
				<SelectPricingCampaigns
					excludePricingCampaigns={[context.pricingCampaignId]}
					rowSelection={rowSelection}
					setRowSelection={setRowSelection}
				/>
				<Alert variant='warning'>
					<InfoIcon />
					<AlertDescription>
						{t('pc.modal.copy_settings_2.note')}
					</AlertDescription>
				</Alert>
			</ModalBody>
			<ModalFooter className='justify-end space-x-2'>
				<ModalClose asChild>
					<Button className='mr-auto'>{t('general_cancel')}</Button>
				</ModalClose>
				<Button onClick={() => context.setStep('settings')}>
					<ChevronLeftIcon />
					{t('general_back')}
				</Button>
				<Button
					variant='primary'
					disabled={Object.keys(rowSelection).length === 0}
					onClick={() => {
						const draft = {
							...context.draft,
							campaigns: Object.keys(rowSelection),
						}
						if (formDataSchema.safeParse(draft).error) {
							toast.error(
								t('pc.modal.copy_settings_3.error', {
									defaultValue: 'Please select at least one pricing campaign',
								})
							)
						} else {
							context.setStep('confirmation')
							context.setDraft(draft)
						}
					}}
				>
					{t('general_next')}
					<ChevronRightIcon />
				</Button>
			</ModalFooter>
		</>
	)
}
function CopyPricingCampaignSettingsConfirmation({
	context,
}: {
	context: ContextValue
}) {
	const { t } = useTranslation()

	const optimizationGroupsQuery = useOptimizationGroupsQuery<
		Record<string, OptimizationGroupModel>
	>({
		select: keyBy<OptimizationGroupModel>('id'),
	})
	const pricingCampaignsQuery = usePricingCampaignsQuery(
		{},
		{
			select: (data) =>
				data.filter((pc) =>
					context.draft.campaigns.includes(pc.id)
				) as PricingCampaignModel[],
		}
	)

	return (
		<>
			<ModalHeader>
				<ModalTitle>{t('pc.modal.copy_settings_2.title')}</ModalTitle>
				<ModalDescription>
					{t('pc.modal.copy_settings_2.subtitle')}
				</ModalDescription>
			</ModalHeader>
			<ModalBody className='space-y-3'>
				<div className='grid grid-cols-2 gap-4'>
					<div className='flex flex-col gap-2'>
						<h4>{t('pc.copy_settings.want_to_copy_settings')}</h4>
						<ul className='max-h-[60vh] min-h-96 list-inside list-disc overflow-y-scroll'>
							{context.draft.settings?.map((name) => (
								<li className='text-sm text-muted-foreground' key={name}>
									{getKeyTitle(name) || 'None'}
								</li>
							))}
						</ul>
					</div>
					<div className='flex flex-col gap-2'>
						<h4>{t('pc.copy_settings.to_following_pcs')}:</h4>
						<ul className='max-h-[60vh] min-h-96 list-inside list-disc overflow-y-scroll'>
							{pricingCampaignsQuery.data?.map(
								({ name, optimization_group_id }) => (
									<li
										className='text-sm text-muted-foreground'
										key={name}
									>{`${name || 'None'} (${optimizationGroupsQuery.data?.[optimization_group_id!]?.name || t('global', { defaultValue: 'Global' })})`}</li>
								)
							)}
						</ul>
					</div>
				</div>
			</ModalBody>
			<ModalFooter className='justify-end space-x-2'>
				<ModalClose asChild>
					<Button className='mr-auto'>{t('general_cancel')}</Button>
				</ModalClose>
				<Button onClick={() => context.setStep('campaigns')}>
					<ChevronLeftIcon />
					{t('general_back')}
				</Button>
				<Button
					variant='primary'
					onClick={() => {
						context.submit()
					}}
				>
					{t('general_next')}
					<ChevronRightIcon />
				</Button>
			</ModalFooter>
		</>
	)
}

const isAllSuccess = (data: PricingCampaignCopySettingsResponseModel[]) =>
	data?.every(prop('success'))
function CopyPricingCampaignSettingsProcessing({
	context,
	mutation,
}: {
	context: ContextValue
	mutation: ReturnType<typeof useCopyPricingCampaignSettingsMutation>
}) {
	const { t } = useTranslation()

	return (
		<>
			<ModalHeader>
				<ModalTitle>{t('pc.modal.copy_settings_4.title')}</ModalTitle>
				<ModalDescription>
					{t('pc.modal.copy_settings_4.subtitle')}
				</ModalDescription>
			</ModalHeader>
			<ModalBody className='min-h-[400px]'>
				{mutation.isPending ? (
					<Empty className='h-full'>
						<EmptyHeader>
							<EmptyMedia variant='icon'>
								<Spinner className='text-muted-foreground' />
							</EmptyMedia>
							<EmptyTitle>{t('pc.processing.loading.title')}</EmptyTitle>
						</EmptyHeader>
					</Empty>
				) : mutation.isError ? (
					<Empty className='h-full'>
						<EmptyHeader>
							<EmptyMedia variant='icon' className='bg-red-500/20'>
								<AlertTriangleIcon className='text-red-500' />
							</EmptyMedia>
							<EmptyTitle>{t('pc.processing.error.title')}</EmptyTitle>
							<EmptyDescription>
								{isString(mutation.error?.response?.data?.detail) &&
									mutation.error?.response?.data?.detail}
							</EmptyDescription>
						</EmptyHeader>
						<EmptyContent>
							<Button
								variant='outline'
								onClick={() => context.setStep('campaigns')}
							>
								<ArrowLeftIcon />
								{t('general_back')}
							</Button>
						</EmptyContent>
					</Empty>
				) : isAllSuccess(mutation.data!) ? (
					<Empty className='h-full'>
						<EmptyHeader>
							<EmptyMedia variant='icon' className='bg-green-500/20'>
								<CheckCircle2Icon className='text-green-500' />
							</EmptyMedia>
							<EmptyTitle>
								{t('app.success', { defaultValue: 'Success' })}
							</EmptyTitle>
							<EmptyDescription>
								{t('pc.settings.processing.success.title', {
									campaigns: context.draft.campaigns.length,
								})}
							</EmptyDescription>
						</EmptyHeader>
					</Empty>
				) : (
					<Empty className='h-full'>
						<EmptyHeader>
							<EmptyMedia variant='icon' className='bg-orange-500/20'>
								<CheckCircle2Icon className='text-orange-500' />
							</EmptyMedia>
							<EmptyTitle>
								{t('app.success', { defaultValue: 'Success' })}
							</EmptyTitle>
							<EmptyDescription>
								{t('pc.settings.processing.indeterminate.title', {
									campaigns: mutation?.data?.filter(prop('success'))?.length,
									totalCampaigns: context.draft.campaigns.length,
								})}
							</EmptyDescription>
						</EmptyHeader>
						<EmptyContent>
							<PricingCampaignManagementResults response={mutation?.data} />
						</EmptyContent>
					</Empty>
				)}
			</ModalBody>
			<ModalFooter className='justify-end space-x-2'>
				<ModalClose asChild>
					<Button variant='primary'>{t('general_close_and_finish')}</Button>
				</ModalClose>
			</ModalFooter>
		</>
	)
}
